import { ViewIcon, ViewOffIcon } from '@chakra-ui/icons';
import {
    FormControl,
    FormLabel,
    IconButton,
    InputGroup,
    InputRightElement,
    Stack,
    VStack,
} from '@chakra-ui/react';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { withTranslation } from 'react-i18next';
import apiClient from '../../api';
import { login } from '../../api/auth';
import * as ga from '../../google-analytics/index';
import { useCurrentColors } from '../../hooks/UseCurrentColors/UseCurrentColors';
import { useCheckForMobile } from '../../hooks/UseResponsive/UseResponsive';
import CanopyButton from '../CanopyButton/CanopyButton';
import CanopyInput from '../CanopyInput/CanopyInput';
import ErrorBox from '../ErrorBox/ErrorBox';
import SuccessBox from '../SuccessBox/SuccessBox';
import { LoginFlow, LoginPageProps } from './LoginPage';

interface LoginFlowProps extends LoginPageProps {
    setLoginFlow: React.Dispatch<React.SetStateAction<LoginFlow>>;
    setLoginResponse: React.Dispatch<
        React.SetStateAction<Record<string, unknown>>
    >;
    success: string;
}

const Login: React.FC<LoginFlowProps> = ({
    userLabel = 'Username',
    passwordLabel = 'Password',
    setLoginFlow,
    setLoginResponse,
    t,
    success,
}) => {
    const isMobile = useCheckForMobile();
    const currentColors = useCurrentColors();

    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    const isSubmitButtonDisabled = Boolean(!(username && password));

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        ga.event({
            action: `Login > Click on "Sign In" `,
            user: apiClient.session,
        });
        event.preventDefault();
        setIsLoading(true);
        try {
            const response = await login({ username, password });
            const { login_flow } = response;
            if (login_flow === 'logged_in') {
                setLoginFlow('logged_in');
            } else if (login_flow === 'reset_password') {
                setLoginResponse({ ...response });
                setLoginFlow('reset_password');
            } else {
                // error
            }
            setIsLoading(false);
        } catch (e: any) {
            if (e?.response?.body) {
                const { body } = e.response;
                setError(body?.error ? body?.error : 'Something went wrong');
            } else if (e?.response?.data) {
                const { data } = e.response;
                if (data.login_flow === '2fa_verification') {
                    setLoginResponse({ ...data, password });
                    setLoginFlow('2fa_verification');
                } else if (data.login_flow === '2fa_activation') {
                    setLoginResponse(data);
                    setLoginFlow('2fa_activation');
                } else if (data.login_flow === 'reset_password') {
                    setLoginResponse({ ...data, password });
                    setLoginFlow('reset_password');
                } else {
                    setError(
                        data?.error ? data?.error : 'Something went wrong'
                    );
                }
            } else {
                setError('Something went wrong');
            }
            setIsLoading(false);
        }
    };
    const handlePasswordVisibility = () => {
        ga.event({
            action: `Login > Click on "Show password" `,
            user: apiClient.session,
        });
        setShowPassword(!showPassword);
    };

    /* const onLanguageChange = useCallback((locale: ILabelValue) => {
        i18next.changeLanguage(locale.value);
    }, []); */

    return (
        <>
            <form
                onSubmit={handleSubmit}
                style={{
                    width: isMobile ? '342px' : 'initial',
                    marginLeft: isMobile ? '7px' : 'initial',
                    marginRight: isMobile ? '7px' : 'initial',
                }}>
                <VStack
                    mt="26px"
                    spacing="26px">
                    {error && (
                        <ErrorBox
                            p="0"
                            m="0"
                            backgroundColor="inherit"
                            color={currentColors.errorText}
                            fontSize="14px"
                            fontWeight="700"
                            position="relative"
                            mb="20px"
                            error={error}></ErrorBox>
                    )}
                    {success && (
                        <SuccessBox
                            p="0"
                            m="0"
                            pl={{ sm: '7px' }}
                            color={currentColors.green['500']}
                            fontSize="14px"
                            fontWeight="700"
                            position="relative"
                            mb="20px"
                            successMessage={success}></SuccessBox>
                    )}
                    <FormControl px={{ sm: '15px' }}>
                        <CanopyInput
                            id="username"
                            width={[
                                '268px',
                                '315px',
                                '315px',
                                '420px',
                                '420px',
                            ]}
                            placeholder={`${t(
                                'ui.analytics-mappings.placeholders.username'
                            )} (e.g john_doe)`}
                            label={userLabel}
                            height="45px"
                            onChange={(
                                event: React.ChangeEvent<HTMLInputElement>
                            ) => setUsername(event.target.value)}></CanopyInput>
                    </FormControl>
                    <FormControl px={{ sm: '15px' }}>
                        <Stack>
                            <FormLabel
                                fontSize="lg"
                                htmlFor="password"
                                color={currentColors.brand['10']}
                                p="0px"
                                fontWeight="bold"
                                lineHeight="unset"
                                mb="7px">
                                {passwordLabel}
                            </FormLabel>
                            <InputGroup
                                width={[
                                    '268px',
                                    '315px',
                                    '315px',
                                    '420px',
                                    '420px',
                                ]}>
                                <CanopyInput
                                    id="password"
                                    placeholder={`${t('enter-password')}`}
                                    type={showPassword ? 'text' : 'password'}
                                    borderTopRightRadius="none"
                                    borderBottomRightRadius="none"
                                    borderRight="none"
                                    width={[
                                        '268px',
                                        '315px',
                                        '315px',
                                        '420px',
                                        '420px',
                                    ]}
                                    mr="0px"
                                    onChange={(
                                        event: React.ChangeEvent<HTMLInputElement>
                                    ) => setPassword(event.target.value)}
                                />
                                <InputRightElement height="45px">
                                    <IconButton
                                        height="45px"
                                        color="inherit"
                                        backgroundColor={
                                            currentColors.brand['900']
                                        }
                                        border="1px"
                                        borderRadius="4px"
                                        borderLeft="0px"
                                        borderTopLeftRadius="none"
                                        borderBottomLeftRadius="none"
                                        borderColor={currentColors.brand['700']}
                                        _hover={{
                                            backgroundColor:
                                                currentColors.brand['900'],
                                            borderColor:
                                                currentColors.brand['700'],
                                        }}
                                        _active={{
                                            backgroundColor:
                                                currentColors.brand['900'],
                                            borderColor:
                                                currentColors.brand['700'],
                                        }}
                                        _focus={{
                                            border: '1px',
                                            borderRadius: '4px',
                                            borderLeft: '0px',
                                            borderTopLeftRadius: 'none',
                                            borderBottomLeftRadius: 'none',
                                            borderColor:
                                                currentColors.brand['700'],
                                        }}
                                        aria-label="Show Password"
                                        onClick={handlePasswordVisibility}
                                        icon={
                                            !showPassword ? (
                                                <ViewOffIcon />
                                            ) : (
                                                <ViewIcon />
                                            )
                                        }
                                    />
                                </InputRightElement>
                            </InputGroup>
                        </Stack>
                    </FormControl>
                    {/* <InputGroup
                        width={['268px', '315px', '315px', '420px', '420px']}>
                        <Stack
                            width="100%"
                            marginBottom="0px">
                            <FormLabel
                                p="0px"
                                fontSize="lg"
                                color={currentColors.brand['10']}
                                fontWeight="bold"
                                lineHeight="unset"
                                mb="7px">
                                {t('language-selector.select-language')}
                            </FormLabel>
                            <LanguageSelector
                                selectedLanguage={getLanguage(i18next.language)}
                                onChange={onLanguageChange}
                            />
                        </Stack>
                    </InputGroup> */}

                    <CanopyButton
                        type="submit"
                        width={['268px', '315px', '315px', '420px', '420px']}
                        height="45px"
                        mt="26px"
                        isLoading={isLoading}
                        isDisabled={isSubmitButtonDisabled}>
                        {t('sign-in')}
                    </CanopyButton>
                </VStack>
            </form>
        </>
    );
};

Login.propTypes = {
    t: PropTypes.func.isRequired,
};

export default withTranslation('translations')(Login);
